import { useState, useEffect, useCallback } from 'react';
import { useUserContext } from '@/hooks/use-user-context';
import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';
import { SupportedLanguage } from '@/lib/language-service';
import { componentLogger } from '@/lib/logging';
import type { Company, UserProfile } from '@/lib/types/core';
import { useUserDetailPermissions } from '@/lib/permissions/hooks/useUserDetailPermissions';
import {
  AssignmentPermission,
  AssignmentListItem,
  NO_COMPANY_SENTINEL as _NO_COMPANY_SENTINEL
} from './UserDialogTypes';

/**
 * Custom hook for user dialog permissions logic
 *
 * Now uses the unified permission hook system for consistent permission handling.
 */
export function useUserDialogPermissions(userToEdit?: UserProfile | null, mode?: 'create' | 'edit') {
  const userContext = useUserContext();

  // Check if current user is a Company Admin (not System Admin)
  const isInvokerCompanyAdmin = userContext?.app_role === 'COMPANY_ADMIN' && !userContext?.is_system_admin;
  const invokerCompanyId = userContext?.company_id;

  // Use unified permission hook for all permission checks
  const { permissions, isLoading: _permissionsLoading, error: _permissionsError } = useUserDetailPermissions(
    userToEdit?.id,
    userToEdit?.app_role || undefined,
    mode
  );

  return {
    userContext,
    isInvokerCompanyAdmin,
    invokerCompanyId,
    permissions,
  };
}

/**
 * Custom hook for managing companies data
 */
export function useCompaniesData(userContext: ReturnType<typeof useUserContext>, isInvokerCompanyAdmin: boolean) {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setCompaniesLoading(true);
        let companiesResult;

        // Determine which companies to fetch based on user role
        if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
          // System Admins can see all companies
          companiesResult = await invokeEdgeFunctionSafely('get-companies');
        } else if (isInvokerCompanyAdmin && userContext?.company_id) {
          // Company Admins can only see their own company
          companiesResult = await invokeEdgeFunctionSafely('get-companies', {
            body: { company_id: userContext.company_id }
          });
        } else {
          // Regular users shouldn't see company options
          setCompanies([]);
          setCompaniesLoading(false);
          return;
        }

        if (companiesResult.error) {
          componentLogger.error('Error fetching companies:', (companiesResult.error as Error).message || 'Unknown error');
          setCompanies([]);
        } else {
          setCompanies((companiesResult.data as { companies: Company[] })?.companies || []);
        }
      } catch (error) {
        componentLogger.error('Error in fetchCompanies:', (error as Error).message || 'Unknown error');
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };

    if (userContext) {
      fetchCompanies();
    }
  }, [userContext, isInvokerCompanyAdmin]);

  return { companies, companiesLoading };
}

/**
 * Custom hook for managing assignment permissions
 */
export function useAssignmentPermissions(
  userToEdit?: UserProfile | null,
  userContext?: ReturnType<typeof useUserContext>,
  isInvokerCompanyAdmin?: boolean,
  invokerCompanyId?: string
) {
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [assignmentPermissions, setAssignmentPermissions] = useState<AssignmentPermission[]>([]);
  // Initialize permissionsLoading as true if we have a user to edit (edit mode)
  const [permissionsLoading, setPermissionsLoading] = useState(!!userToEdit?.id);
  const [originalPermissions, setOriginalPermissions] = useState<AssignmentPermission[]>([]);
  const [syncingPermissions, setSyncingPermissions] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  // Fetch available assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        setAssignmentsLoading(true);
        const { data: assignmentsData, error: assignmentsError } = await invokeEdgeFunctionSafely(
          'get-assignments-for-permissions'
        );

        if (assignmentsError) {
          componentLogger.error('Error fetching assignments:', (assignmentsError as Error).message || 'Unknown error');
          setAssignments([]);
        } else {
          setAssignments(assignmentsData as AssignmentListItem[] || []);
        }
      } catch (error) {
        componentLogger.error('Error in fetchAssignments:', (error as Error).message || 'Unknown error');
        setAssignments([]);
      } finally {
        setAssignmentsLoading(false);
      }
    };

    if (userContext) {
      fetchAssignments();
    } else {
      setAssignmentsLoading(false);
    }
  }, [userContext, isInvokerCompanyAdmin, invokerCompanyId]);

  // Load existing user permissions with email preferences
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!userToEdit?.id) {
        setPermissionsLoading(false);
        return;
      }

      // Wait for assignments to load first to ensure fallback works
      if (assignmentsLoading) {
        return;
      }

      try {
        setPermissionsLoading(true);
        const { data, error } = await invokeEdgeFunctionSafely(
          'get-user-permissions-with-email',
          { body: { userId: userToEdit.id } }
        );

        if (error) {
          componentLogger.error('Error loading user permissions with email:', (error as Error).message || 'Unknown error');
          return;
        }

        const permissions = (data as Record<string, unknown>[] || []).map((item: Record<string, unknown>) => {
          const assignmentName = item['assignment_name'] as string;
          const assignmentId = item['assignment_id'] as string;

          // If assignment name is missing, try to find it in the assignments list
          let finalAssignmentName = assignmentName;
          if (!finalAssignmentName && assignments.length > 0) {
            const assignment = assignments.find(a => a.id === assignmentId);
            finalAssignmentName = assignment?.name || 'Unknown Assignment';
          }

          return {
            assignmentId,
            assignmentName: finalAssignmentName || 'Unknown Assignment',
            permissionLevel: item['permission_level'] as string,
            emailNotifications: item['email_notifications'] as boolean || false, // Now included from edge function
          };
        }) as AssignmentPermission[];

        setAssignmentPermissions(permissions);
        setOriginalPermissions([...permissions]);
      } catch (error) {
        componentLogger.error('Error loading user permissions with email:', (error as Error).message, { error });
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [userToEdit, assignments, assignmentsLoading]);

  // Permission management functions
  const addAssignmentPermission = useCallback((assignmentId: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return;

    const newPermission: AssignmentPermission = {
      assignmentId,
      assignmentName: assignment.name,
      permissionLevel: 'VIEWER',
      emailNotifications: true,  // Default to TRUE for new project assignments
    };

    setAssignmentPermissions(prev => [...prev, newPermission]);
  }, [assignments]);

  const removeAssignmentPermission = useCallback((assignmentId: string) => {
    setAssignmentPermissions(prev => prev.filter(p => p.assignmentId !== assignmentId));
  }, []);

  const updateAssignmentPermissionLevel = useCallback((assignmentId: string, level: string) => {
    setAssignmentPermissions(prev =>
      prev.map(p => p.assignmentId === assignmentId ? { ...p, permissionLevel: level } : p) as AssignmentPermission[]
    );
  }, []);

  const updateEmailNotificationPreference = useCallback((assignmentId: string, enabled: boolean) => {
    setAssignmentPermissions(prev =>
      prev.map(p => p.assignmentId === assignmentId ? { ...p, emailNotifications: enabled } : p)
    );
  }, []);

  return {
    assignments,
    assignmentsLoading,
    assignmentPermissions,
    permissionsLoading,
    originalPermissions,
    syncingPermissions,
    syncProgress,
    setSyncingPermissions,
    setSyncProgress,
    addAssignmentPermission,
    removeAssignmentPermission,
    updateAssignmentPermissionLevel,
    updateEmailNotificationPreference,
    setAssignmentPermissions,
  };
}

/**
 * Helper function to get default language for user
 */
export function getDefaultLanguage(userToEdit?: UserProfile | null, userContext?: ReturnType<typeof useUserContext>): SupportedLanguage {
  if (userToEdit?.language) {
    return (userToEdit.language as SupportedLanguage) ||
      ((userContext as unknown as { company?: { default_language?: unknown } })?.company?.default_language as SupportedLanguage) ||
      'en';
  } else {
    return ((userContext as unknown as { company?: { default_language?: unknown } })?.company?.default_language as SupportedLanguage) || 'en';
  }
}

/**
 * Helper function to get available role options based on current user permissions
 */
export function getAvailableRoleOptions(userContext?: ReturnType<typeof useUserContext>) {
  if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
    return [
      { value: 'USER', label: 'User' },
      { value: 'COMPANY_ADMIN', label: 'Company Admin' },
      { value: 'ADMIN', label: 'System Admin' },
    ];
  } else if (userContext?.app_role === 'COMPANY_ADMIN') {
    return [
      { value: 'USER', label: 'User' },
      { value: 'COMPANY_ADMIN', label: 'Company Admin' },
    ];
  }
  return [{ value: 'USER', label: 'User' }];
}